import { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { dashboardApi } from '../services/dashboardApi';

// ============================================
// Types
// ============================================
interface EspStat {
  sent: number;
  opens: number;
  clicks: number;
  open_rate: number;
  click_rate: number;
}

interface CampaignRow {
  campaign_id: string;
  campaign_name: string;
  send_date: string;
  total_sent: number;
  geo: string;
  esp_stats: Record<string, EspStat>;
}

const ESP_LABELS: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  yahoo: 'Yahoo',
  other: 'Other',
};

const ESP_KEYS = ['google', 'microsoft', 'yahoo', 'other'];

const GEO_OPTIONS = [
  { value: 'all', label: 'All Geos' },
  { value: 'no', label: '🇳🇴 Norway' },
  { value: 'se', label: '🇸🇪 Sweden' },
  { value: 'fi', label: '🇫🇮 Finland' },
  { value: 'de', label: '🇩🇪 Germany' },
];

const DAYS_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
];

// ============================================
// Helpers
// ============================================
function formatDate(iso: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Copenhagen',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function rateColor(rate: number): string {
  if (rate >= 0.25) return 'text-green-600';
  if (rate >= 0.15) return 'text-yellow-600';
  return 'text-red-500';
}

function rateBg(rate: number): string {
  if (rate >= 0.25) return 'bg-green-50';
  if (rate >= 0.15) return 'bg-yellow-50';
  return 'bg-red-50';
}

// ============================================
// Main Component
// ============================================
export default function WebhookCampaignStatsPage() {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geo, setGeo] = useState('all');
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await dashboardApi.getWebhookCampaignStats(geo, days);
      setCampaigns(resp.campaigns || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load campaign stats');
    } finally {
      setLoading(false);
    }
  }, [geo, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute summary KPIs
  const totalCampaigns = campaigns.length;
  let totalOpens = 0;
  let totalClicks = 0;
  let totalSent = 0;
  campaigns.forEach(c => {
    Object.values(c.esp_stats).forEach(s => {
      totalOpens += s.opens;
      totalClicks += s.clicks;
      totalSent += s.sent;
    });
  });

  return (
    <MainLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header + Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📬 Campaign Stats by ESP</h1>
            <p className="text-gray-500 text-sm mt-1">
              Opens &amp; clicks from the webhook daily campaigns, broken down by email provider
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              id="geo-filter"
              value={geo}
              onChange={e => setGeo(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {GEO_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              id="days-filter"
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {DAYS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading spinner */}
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <strong>Error:</strong> {error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border">
                <p className="text-sm font-medium text-gray-500 mb-1">Campaigns</p>
                <p className="text-3xl font-bold text-gray-900">{totalCampaigns.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Sent</p>
                <p className="text-3xl font-bold text-gray-900">{totalSent.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Opens</p>
                <p className="text-3xl font-bold text-indigo-600">{totalOpens.toLocaleString()}</p>
                {totalSent > 0 && (
                  <p className="text-xs text-gray-400 mt-1">{((totalOpens / totalSent) * 100).toFixed(1)}% open rate</p>
                )}
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Clicks</p>
                <p className="text-3xl font-bold text-emerald-600">{totalClicks.toLocaleString()}</p>
                {totalSent > 0 && (
                  <p className="text-xs text-gray-400 mt-1">{((totalClicks / totalSent) * 100).toFixed(1)}% click rate</p>
                )}
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Campaign</th>
                      <th className="text-center py-3 px-3 font-semibold text-gray-600">Geo</th>
                      <th className="text-right py-3 px-3 font-semibold text-gray-600">Sent</th>
                      {ESP_KEYS.map(esp => (
                        <th key={esp} colSpan={3} className="text-center py-3 px-2 font-semibold text-gray-600 border-l border-gray-200">
                          {ESP_LABELS[esp]}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-gray-50 border-b text-xs text-gray-400">
                      <th className="sticky left-0 bg-gray-50 z-10"></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      {ESP_KEYS.map(esp => (
                        <>
                          <th key={`${esp}-o`} className="py-1 px-2 border-l border-gray-200 font-medium">Opens</th>
                          <th key={`${esp}-c`} className="py-1 px-2 font-medium">Clicks</th>
                          <th key={`${esp}-r`} className="py-1 px-2 font-medium">OR%</th>
                        </>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 ? (
                      <tr>
                        <td colSpan={4 + ESP_KEYS.length * 3} className="text-center py-12 text-gray-400">
                          No campaign data found for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      campaigns.map((c, i) => (
                        <tr
                          key={`${c.campaign_id}-${i}`}
                          className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="py-2.5 px-4 text-gray-600 font-mono text-xs whitespace-nowrap sticky left-0 bg-white z-10">
                            {formatDate(c.send_date)}
                          </td>
                          <td className="py-2.5 px-4 text-gray-800 font-medium max-w-[200px] truncate" title={c.campaign_name}>
                            {c.campaign_name || c.campaign_id}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                              {c.geo}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right text-gray-600 font-medium">
                            {(c.total_sent || 0).toLocaleString()}
                          </td>
                          {ESP_KEYS.map(esp => {
                            const s = c.esp_stats[esp];
                            if (!s) {
                              return (
                                <>
                                  <td key={`${c.campaign_id}-${esp}-o`} className="py-2.5 px-2 text-center text-gray-300 border-l border-gray-100">—</td>
                                  <td key={`${c.campaign_id}-${esp}-c`} className="py-2.5 px-2 text-center text-gray-300">—</td>
                                  <td key={`${c.campaign_id}-${esp}-r`} className="py-2.5 px-2 text-center text-gray-300">—</td>
                                </>
                              );
                            }
                            return (
                              <>
                                <td key={`${c.campaign_id}-${esp}-o`} className="py-2.5 px-2 text-right text-gray-700 border-l border-gray-100 font-medium">
                                  {s.opens.toLocaleString()}
                                </td>
                                <td key={`${c.campaign_id}-${esp}-c`} className="py-2.5 px-2 text-right text-gray-700 font-medium">
                                  {s.clicks.toLocaleString()}
                                </td>
                                <td key={`${c.campaign_id}-${esp}-r`} className={`py-2.5 px-2 text-right font-semibold ${rateColor(s.open_rate)}`}>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${rateBg(s.open_rate)}`}>
                                    {(s.open_rate * 100).toFixed(1)}%
                                  </span>
                                </td>
                              </>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
